import Dropdown from "core/dropdown/Dropdown";
import DropdownLinkItem from "core/dropdown/DropdownLinkItem";
import { FormattedMessage } from "react-intl";
import { useLayoutEffect, useState, useEffect } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { MANAGER_TOOLS_PATH } from "constants/constants";

// Menus whose parent link keeps its own destination instead of falling back on
// the first dropdown item.
const PARENT_LINK_MENU_IDS = ["spreadsheet", "manager"];

// Module segment of every destination a menu leads to, parent and dropdown
// items alike ("/perso/delivery-notes/templates" -> "delivery-notes").
const getMenuModuleSegments = (menu) =>
  new Set(
    [menu.path, ...(menu.dropdown_menu?.map((item) => item.path) ?? [])]
      .flat()
      .filter(Boolean)
      .map((path) => path.split("/")[2])
  );

const HoverMenuButton = ({
  defaultMessage,
  has_dropdown,
  menu,
  menu_index,
  translationId,
  scope,
}) => {
  const [isOverButton, setIsOverButton] = useState(false);
  const [isOverList, setIsOverList] = useState(false);
  const [isOpen, setIsOpen] = useState();
  const [isTouchInput, setIsTouchInput] = useState();
  const [hasClicked, setHasClicked] = useState();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [activeClass, setActiveClass] = useState("parent");

  useLayoutEffect(() => {
    setIsOpen((isOverButton || isOverList) && !isTouchInput);
  }, [isOverButton, isOverList, isTouchInput]);

  useEffect(() => {
    setIsTouchInput(false);
    setHasClicked(false);
  }, [hasClicked]);

  const menuUrls = {
    spreadsheet: `${scope}/inputs/spreadsheet`,
    dash: `${scope}/dashboard`,
    missions: `${scope}/missions`,
    training: `${scope}/training`,
    manager: `${scope}/${MANAGER_TOOLS_PATH}`,
  };

  const getUrl = () => {
    if (PARENT_LINK_MENU_IDS.includes(menu.id) && menuUrls[menu.id]) {
      return menuUrls[menu.id];
    }
    if (menuUrls[menu.id]) {
      if (!menu.has_dropdown) {
        return menuUrls[menu.id];
      }
    }
    if (menu.dropdown_menu?.length) {
      const firstSubItem = menu.dropdown_menu[0];
      return `${scope}/${firstSubItem.dropdownItemLink}`;
    }
    return "/default";
  };

  useEffect(() => {
    const topHeaderNavTab = pathname.split("/")[2];
    const inputsLinks = ["spreadsheet", "import-billing"];

    if (
      topHeaderNavTab?.includes(menu.id) ||
      (topHeaderNavTab === "inputs" && inputsLinks.includes(menu.id)) ||
      // A dropdown item may point to another module than its parent (delivery
      // notes hang from the manager tools menu): the declared destinations say
      // which pages keep the parent highlighted, no id has to be hardcoded.
      getMenuModuleSegments(menu).has(topHeaderNavTab)
    ) {
      return setActiveClass("parent active");
    }
    return setActiveClass("parent");
  }, [pathname]);

  return (
    <div
      onMouseEnter={() => menu.dropdown_menu && setIsOverButton(true)}
      onMouseLeave={() => menu.dropdown_menu && setIsOverButton(false)}
    >
      <NavLink to={getUrl()} className={activeClass}>
        <FormattedMessage id={translationId} defaultMessage={defaultMessage} />
      </NavLink>
      {has_dropdown && (
        <div
          className={`navbar-link__dropdown ${isOpen && "active"}`}
          onMouseEnter={() => {
            setIsOverList(true);
          }}
          onMouseLeave={() => {
            setIsOverList(false);
          }}
        >
          <Dropdown
            id={`menu-${menu_index}`}
            width={"max-content"}
            marginLeft={-35}
            onMouseEnter={() => {
              setIsOverList(true);
            }}
            onMouseLeave={() => {
              setIsOverList(false);
            }}
          >
            {menu.dropdown_menu &&
              menu.dropdown_menu.map((dropdown_element) => {
                return (
                  <DropdownLinkItem
                    to={`${scope}/${dropdown_element.dropdownItemLink}`}
                    onClick={() => {
                      navigate(`${scope}/${dropdown_element.dropdownItemLink}`);
                      setIsOpen(false);
                    }}
                    key={dropdown_element.translationId}
                    id={dropdown_element.translationId}
                  >
                    <FormattedMessage
                      id={dropdown_element.translationId}
                      defaultMessage={dropdown_element.defaultMessage}
                    />
                  </DropdownLinkItem>
                );
              })}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

HoverMenuButton.propTypes = {
  defaultMessage: PropTypes.string,
  has_dropdown: PropTypes.bool,
  menu: PropTypes.shape({
    id: PropTypes.string,
    has_dropdown: PropTypes.bool,
    dropdown_menu: PropTypes.arrayOf(
      PropTypes.shape({
        dropdownItemLink: PropTypes.string,
        translationId: PropTypes.string,
        defaultMessage: PropTypes.string,
      })
    ),
  }),
  menu_index: PropTypes.number,
  translationId: PropTypes.string,
  scope: PropTypes.string,
};

export default HoverMenuButton;
